import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _regeneratorRuntime from "@babel/runtime/regenerator";
import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { getTabs } from '@modals/add-new-group/add-new-group.helpers';
import { getAddNewGroupSchema } from '@modals/add-new-group/add-new-group.schema';
import { Box, Button, Grid, Typography } from '@mui/material';
import { getUserSelector } from '@store/auth';
import { getModalSelector } from '@store/modals';
import { GroupsService, ModalsService, PopupsService } from '@services';
import { getEnding } from '@utils';
import { useApi } from '@hooks';
import { Ending, ErrorsEnum, Modals, PermissionEnum, Popups } from '@enums';
import { CheckboxField, CustomTabPanel, Form, FormField, Modal, TextField } from '@elements';
import { Tabs } from '@elements/tabs';
import { StyledCheckBoxContainer, StyledDivider, StyledScroll } from './add-new-group.styles';
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";

/**
 * Add new group modal component.
 *
 * @author Ihar Kazlouski
 * @function AddNewGroupModal
 * @category Modals
 * @return {FC} add new group modal component.
 */
var AddNewGroupModal = function AddNewGroupModal() {
  var _useTranslation = useTranslation(),
      t = _useTranslation.t;

  var modal = useSelector(getModalSelector(Modals.AddNewGroup));
  var authUser = useSelector(getUserSelector);
  var modalData = modal === null || modal === void 0 ? void 0 : modal.data;
  var formRef = useRef();

  var _useState = useState(t('components.modals.addNewGroup.general')),
      _useState2 = _slicedToArray(_useState, 2),
      value = _useState2[0],
      setValue = _useState2[1];

  var _useState3 = useState(null),
      _useState4 = _slicedToArray(_useState3, 2),
      error = _useState4[0],
      setError = _useState4[1];

  var _useApi = useApi({
    api: GroupsService.apiAdminUsersGet
  }),
      users = _useApi.data,
      usersError = _useApi.error,
      usersLoading = _useApi.loading;

  var initialValues = useMemo(function () {
    var _modalData$users, _modalData$roles, _modalData$users2;

    return {
      users: (modalData === null || modalData === void 0 ? void 0 : (_modalData$users = modalData.users) === null || _modalData$users === void 0 ? void 0 : _modalData$users.map(function (user) {
        return user.id;
      })) || [],
      groupName: (modalData === null || modalData === void 0 ? void 0 : modalData.group_name) || '',
      permissions: (modalData === null || modalData === void 0 ? void 0 : (_modalData$roles = modalData.roles) === null || _modalData$roles === void 0 ? void 0 : _modalData$roles.map(function (permission) {
        return permission.id;
      })) || [],
      selectAll: (modalData === null || modalData === void 0 ? void 0 : (_modalData$users2 = modalData.users) === null || _modalData$users2 === void 0 ? void 0 : _modalData$users2.length) === (users === null || users === void 0 ? void 0 : users.length) || false
    };
  }, [modalData === null || modalData === void 0 ? void 0 : modalData.isEdit, modalData === null || modalData === void 0 ? void 0 : modalData.group_name, usersLoading]);
  /**
   * Handle change tab.
   *
   * @author Ihar Kazlouski
   * @param {SyntheticEvent} event event.
   * @param {string} newValue new value.
   * @return {Promise<void>}
   */

  var handleChange = function handleChange(event, newValue) {
    setValue(newValue);
  };

  var handleSuccess = function handleSuccess() {
    PopupsService.open(Popups.Success, {
      header: t('components.popups.success'),
      body: modalData !== null && modalData !== void 0 && modalData.isEdit ? t('components.modals.addNewGroup.groupUpdated') : t('components.modals.addNewGroup.groupAdded'),
      acceptButton: t('components.popups.ok'),
      width: 270,
      maxWidth: 'xs'
    }).then(function () {
      ModalsService.close(Modals.AddNewGroup);
      setValue(t('components.modals.addNewGroup.general'));
      modalData.updateAfterEdit();
    })["catch"](function () {
      modalData.updateAfterEdit();
    });
  };
  /**
   * Submit form.
   *
   * @author Ihar Kazlouski
   * @param {AddNewGroupValues} values values.
   * @return {Promise<void>}
   */


  var handleSubmit = function handleSubmit(values) {
    return function () {
      void _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
        var _error, _error2;

        return _regeneratorRuntime.wrap(function _callee$(_context) {
          while (1) {
            switch (_context.prev = _context.next) {
              case 0:
                if (!(modalData !== null && modalData !== void 0 && modalData.isEdit)) {
                  _context.next = 13;
                  break;
                }

                _context.prev = 1;
                _context.next = 4;
                return GroupsService.apiUpdateAdminGroupsPut(authUser === null || authUser === void 0 ? void 0 : authUser.id, {
                  body: {
                    name: values.groupName,
                    role_ids: values.permissions,
                    user_ids: values.users,
                    id: modalData === null || modalData === void 0 ? void 0 : modalData.id
                  }
                });

              case 4:
                handleSuccess();
                _context.next = 11;
                break;

              case 7:
                _context.prev = 7;
                _context.t0 = _context["catch"](1);
                _error = _context.t0;

                if (_error.errorCode === ErrorsEnum.GroupNameIsAlreadyExists) {
                  setValue(t('components.modals.addNewGroup.general'));
                  setError(t('components.modals.addNewGroup.nameError'));
                } else {
                  void PopupsService.open(Popups.Error, {
                    header: t('components.popups.errorTitle'),
                    body: t('components.modals.addNewGroup.updateError'),
                    acceptButton: t('components.popups.ok'),
                    width: 270,
                    maxWidth: 'xs'
                  })["catch"](function () {// error handler
                  });
                }

              case 11:
                _context.next = 23;
                break;

              case 13:
                _context.prev = 13;
                _context.next = 16;
                return GroupsService.apiAdminGroupCreatePost((authUser === null || authUser === void 0 ? void 0 : authUser.id) || '', {
                  body: {
                    name: values.groupName,
                    role_ids: values.permissions,
                    user_ids: values.users
                  }
                });

              case 16:
                handleSuccess();
                _context.next = 23;
                break;

              case 19:
                _context.prev = 19;
                _context.t1 = _context["catch"](13);
                _error2 = _context.t1;

                if (_error2.errorCode === ErrorsEnum.GroupNameIsAlreadyExists) {
                  setValue(t('components.modals.addNewGroup.general'));
                  setError(t('components.modals.addNewGroup.nameError'));
                } else {
                  void PopupsService.open(Popups.Error, {
                    header: t('components.popups.errorTitle'),
                    body: t('components.modals.addNewGroup.addError'),
                    acceptButton: t('components.popups.ok'),
                    width: 270,
                    maxWidth: 'xs'
                  })["catch"](function () {// error handler
                  });
                }

              case 23:
              case "end":
                return _context.stop();
            }
          }
        }, _callee, null, [[1, 7], [13, 19]]);
      }))();
    };
  };
  /**
   * Select all handler.
   *
   * @author Ihar Kazlouski
   * @param {AddNewGroupValues} values values.
   * @param {FormApi<AddNewGroupValues>} form form.
   * @return {Promise<void>}
   */


  var selectAll = function selectAll(values, form) {
    return function () {
      var filteredUsers = users === null || users === void 0 ? void 0 : users.filter(function (el) {
        return el.user_name.includes(values.search || '');
      });

      if (values.users.length === (filteredUsers === null || filteredUsers === void 0 ? void 0 : filteredUsers.length)) {
        form.change('users', []);
        form.change('selectAll', false);
      } else {
        form.change('users', (filteredUsers === null || filteredUsers === void 0 ? void 0 : filteredUsers.map(function (user) {
          return user === null || user === void 0 ? void 0 : user.id;
        })) || []);
        form.change('selectAll', true);
      }
    };
  };
  /**
   * Select users handler.
   *
   * @author Ruslan Kolesov
   * @param {AddNewGroupValues} values values.
   * @param {FormApi<AddNewGroupValues>} form form.
   * @return {Promise<void>}
   */


  var selectUsers = function selectUsers(values, form) {
    return function () {
      var filteredUsers = users ? users === null || users === void 0 ? void 0 : users.filter(function (el) {
        return el.user_name.includes(values.search || '');
      }) : [];
      var checkedUsersLength = form.getState().values.users.length;

      if (checkedUsersLength === filteredUsers.length) {
        form.change('selectAll', true);
      } else {
        form.change('selectAll', false);
      }
    };
  };
  /**
   * Search handler.
   *
   * @author Ruslan Kolesov
   * @param {FormApi<AddNewGroupValues>} form form.
   * @return {Promise<void>}
   */


  var searchHandler = function searchHandler(form) {
    return function () {
      var formValues = form.getState().values;
      var filteredUsers = users === null || users === void 0 ? void 0 : users.filter(function (el) {
        return el.user_name.toLowerCase().includes(formValues.search || '');
      });
      var res = formValues.users.filter(function (id) {
        return filteredUsers === null || filteredUsers === void 0 ? void 0 : filteredUsers.find(function (el) {
          return el.id === id;
        });
      }); // form.change('users', res?.map((id) => id) || []);

      if ((res === null || res === void 0 ? void 0 : res.length) === (filteredUsers === null || filteredUsers === void 0 ? void 0 : filteredUsers.length)) {
        form.change('selectAll', true);
      } else {
        form.change('selectAll', false);
      }
    };
  };
  /**
   * Close modal.
   *
   * @author Ihar Kazlouski
   * @return {void}
   */


  var closeModal = function closeModal() {
    var _formRef$current, _formRef$current$getS;

    if (!(formRef !== null && formRef !== void 0 && (_formRef$current = formRef.current) !== null && _formRef$current !== void 0 && (_formRef$current$getS = _formRef$current.getState()) !== null && _formRef$current$getS !== void 0 && _formRef$current$getS.pristine)) {
      PopupsService.open(Popups.Default, {
        header: t('components.popups.unsavedChanges'),
        body: modalData !== null && modalData !== void 0 && modalData.isEdit ? t('components.modals.addNewGroup.quitEdit') : t('components.modals.addNewGroup.quitAdd'),
        acceptButton: t('components.popups.yes'),
        rejectButton: t('components.popups.cancel'),
        maxWidth: 'xs',
        width: 270
      }).then(function () {
        ModalsService.close(Modals.AddNewGroup);
        setValue(t('components.modals.addNewGroup.general'));
      })["catch"](function () {// error handling
      });
    } else {
      ModalsService.close(Modals.AddNewGroup);
      setValue(t('components.modals.addNewGroup.general'));
    }
  };
  /**
   * Read-only checkbox handler.
   *
   * @author Ihar Kazlouski
   * @param {AddNewGroupValues} values values.
   * @param {FormApi<AddNewGroupValues>} form form.
   * @return {Promise<void>}
   */


  var handleReadOnlyChange = function handleReadOnlyChange(values, form) {
    return function () {
      var _modalData$permission;

      if (values.permissions.includes((modalData === null || modalData === void 0 ? void 0 : (_modalData$permission = modalData.permissionOptions.find(function (permission) {
        return permission.role_name === 'READ_ONLY';
      })) === null || _modalData$permission === void 0 ? void 0 : _modalData$permission.id) || '')) {
        form.change('permissions', []);
      } else {
        var _modalData$permission2, _modalData$permission3;

        form.change('permissions', [(modalData === null || modalData === void 0 ? void 0 : (_modalData$permission2 = modalData.permissionOptions) === null || _modalData$permission2 === void 0 ? void 0 : (_modalData$permission3 = _modalData$permission2.find(function (permission) {
          return permission.role_name === 'READ_ONLY';
        })) === null || _modalData$permission3 === void 0 ? void 0 : _modalData$permission3.id) || '']);
      }
    };
  };

  var handlePermissionChange = function handlePermissionChange(form) {
    return function () {
      var formState = form.getState();
      form.change('permissions', formState.values.permissions.filter(function (item) {
        var _modalData$permission4;

        return item !== (modalData === null || modalData === void 0 ? void 0 : (_modalData$permission4 = modalData.permissionOptions.find(function (permission) {
          return permission.role_name === 'READ_ONLY';
        })) === null || _modalData$permission4 === void 0 ? void 0 : _modalData$permission4.id);
      }));
    };
  };
  /**
   * Parse script tags.
   *
   * @author Ihar Kazlouski
   * @param {unknown} value value.
   * @return {void}
   */


  var handleScriptParse = function handleScriptParse(value) {
    var parseValue = value;
    var regex = /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi;
    return parseValue.replace(regex, '');
  };

  if (usersError) {
    void PopupsService.open(Popups.Default, {
      header: t('components.popups.errorOccurred'),
      body: t('components.popups.errorText'),
      acceptButton: t('components.popups.ok')
    })["catch"](function () {// ToDo: error handling
    });
    return /*#__PURE__*/_jsx(_Fragment, {});
  }
  /**
   * Handle change function.
   *
   * @author Ihar Kazlouski
   * @return {void}
   */


  var handleGroupNameChange = function handleGroupNameChange() {
    if (error) {
      setError(null);
    }
  };

  return /*#__PURE__*/_jsxs(Modal, {
    fullWidth: true,
    maxWidth: "md",
    sx: {
      position: 'relative'
    },
    children: [/*#__PURE__*/_jsx(Modal.Title, {
      handleClose: closeModal,
      children: modalData !== null && modalData !== void 0 && modalData.isEdit ? t('components.modals.addNewGroup.editGroup') : t('components.modals.addNewGroup.addGroup')
    }), /*#__PURE__*/_jsx(Modal.Content, {
      children: /*#__PURE__*/_jsx(_Fragment, {
        children: /*#__PURE__*/_jsx(Form, {
          onSubmit: handleSubmit,
          subscription: {
            pristine: true,
            submitting: true,
            invalid: true,
            values: true
          },
          initialValues: initialValues,
          validateSchema: getAddNewGroupSchema(error),
          render: function render(_ref2) {
            var _modalData$permission5, _modalData$permission6, _modalData$permission7, _modalData$permission8;

            var pristine = _ref2.pristine,
                submitting = _ref2.submitting,
                invalid = _ref2.invalid,
                values = _ref2.values,
                form = _ref2.form;
            formRef.current = form;
            var filteredUsers = users === null || users === void 0 ? void 0 : users.filter(function (el) {
              return el.user_name.toLowerCase().includes((values.search || '').toLowerCase());
            });
            return /*#__PURE__*/_jsxs(_Fragment, {
              children: [/*#__PURE__*/_jsx(Box, {
                pt: 4,
                pb: 4,
                children: /*#__PURE__*/_jsx(Tabs, {
                  value: value,
                  onChange: handleChange,
                  getTabs: getTabs(values.users.length)
                })
              }), /*#__PURE__*/_jsxs(CustomTabPanel, {
                value: value,
                index: t('components.modals.addNewGroup.general'),
                children: [/*#__PURE__*/_jsx(Grid, {
                  container: true,
                  spacing: 2,
                  children: /*#__PURE__*/_jsx(Grid, {
                    item: true,
                    xs: 5,
                    children: /*#__PURE__*/_jsx(FormField, {
                      name: "groupName",
                      size: "small",
                      component: TextField,
                      parse: handleScriptParse,
                      onChange: handleGroupNameChange,
                      disabled: (modalData === null || modalData === void 0 ? void 0 : modalData.isEdit) && values.groupName === 'Registrar',
                      inputProps: {
                        maxLength: 100
                      },
                      fullWidth: true,
                      label: t('components.modals.addNewGroup.groupName')
                    })
                  })
                }), /*#__PURE__*/_jsx(Box, {
                  paddingY: 5,
                  children: /*#__PURE__*/_jsx(Typography, {
                    variant: "body2",
                    color: "comet",
                    children: t('components.modals.addNewGroup.permissions')
                  })
                }), /*#__PURE__*/_jsxs(Grid, {
                  container: true,
                  spacing: 3,
                  children: [/*#__PURE__*/_jsxs(Grid, {
                    item: true,
                    xs: 6,
                    display: "flex",
                    alignItems: "center",
                    children: [/*#__PURE__*/_jsx(FormField, {
                      name: "permissions",
                      component: CheckboxField,
                      variant: "outlined",
                      type: "checkbox",
                      iconColor: "frenchGray",
                      onChange: handlePermissionChange(form),
                      value: (modalData === null || modalData === void 0 ? void 0 : (_modalData$permission5 = modalData.permissionOptions.find(function (permission) {
                        return permission.role_name === 'MANAGE_ADMIN_CONSOLE_USERS';
                      })) === null || _modalData$permission5 === void 0 ? void 0 : _modalData$permission5.id) || '',
                      disabled: (modalData === null || modalData === void 0 ? void 0 : modalData.isEdit) && values.groupName === 'Registrar'
                    }), /*#__PURE__*/_jsx(Typography, {
                      ml: 1.25,
                      variant: "body1",
                      color: "mineShaft",
                      fontWeight: "500",
                      children: PermissionEnum.MANAGE_ADMIN_CONSOLE_USERS
                    })]
                  }), /*#__PURE__*/_jsxs(Grid, {
                    item: true,
                    xs: 6,
                    display: "flex",
                    alignItems: "center",
                    children: [/*#__PURE__*/_jsx(FormField, {
                      name: "permissions",
                      variant: "outlined",
                      type: "checkbox",
                      iconColor: "frenchGray",
                      component: CheckboxField,
                      onChange: handlePermissionChange(form),
                      value: (modalData === null || modalData === void 0 ? void 0 : (_modalData$permission6 = modalData.permissionOptions.find(function (permission) {
                        return permission.role_name === 'MANAGE_WISPER_USERS';
                      })) === null || _modalData$permission6 === void 0 ? void 0 : _modalData$permission6.id) || '',
                      disabled: (modalData === null || modalData === void 0 ? void 0 : modalData.isEdit) && values.groupName === 'Registrar'
                    }), /*#__PURE__*/_jsx(Typography, {
                      ml: 1.25,
                      variant: "body1",
                      color: "mineShaft",
                      fontWeight: "500",
                      children: PermissionEnum.MANAGE_WISPER_USERS
                    })]
                  }), /*#__PURE__*/_jsxs(Grid, {
                    item: true,
                    xs: 6,
                    display: "flex",
                    alignItems: "center",
                    children: [/*#__PURE__*/_jsx(FormField, {
                      variant: "outlined",
                      type: "checkbox",
                      iconColor: "frenchGray",
                      name: "permissions",
                      component: CheckboxField,
                      onChange: handlePermissionChange(form),
                      value: (modalData === null || modalData === void 0 ? void 0 : (_modalData$permission7 = modalData.permissionOptions.find(function (permission) {
                        return permission.role_name === 'MANAGE_ADMIN_CONSOLE_GROUPS';
                      })) === null || _modalData$permission7 === void 0 ? void 0 : _modalData$permission7.id) || '',
                      disabled: (modalData === null || modalData === void 0 ? void 0 : modalData.isEdit) && values.groupName === 'Registrar'
                    }), /*#__PURE__*/_jsx(Typography, {
                      ml: 1.25,
                      variant: "body1",
                      color: "mineShaft",
                      fontWeight: "500",
                      children: PermissionEnum.MANAGE_ADMIN_CONSOLE_GROUPS
                    })]
                  }), /*#__PURE__*/_jsxs(Grid, {
                    item: true,
                    xs: 6,
                    display: "flex",
                    alignItems: "center",
                    children: [/*#__PURE__*/_jsx(FormField, {
                      variant: "outlined",
                      type: "checkbox",
                      iconColor: "frenchGray",
                      name: "permissions",
                      component: CheckboxField,
                      onChange: handleReadOnlyChange(values, form),
                      disabled: (modalData === null || modalData === void 0 ? void 0 : modalData.isEdit) && values.groupName === 'Registrar',
                      value: (modalData === null || modalData === void 0 ? void 0 : (_modalData$permission8 = modalData.permissionOptions.find(function (permission) {
                        return permission.role_name === 'READ_ONLY';
                      })) === null || _modalData$permission8 === void 0 ? void 0 : _modalData$permission8.id) || ''
                    }), /*#__PURE__*/_jsx(Typography, {
                      ml: 1.25,
                      variant: "body1",
                      color: "mineShaft",
                      fontWeight: "500",
                      children: PermissionEnum.READ_ONLY
                    })]
                  })]
                })]
              }), /*#__PURE__*/_jsx(CustomTabPanel, {
                value: value,
                index: t('components.modals.addNewGroup.members'),
                children: /*#__PURE__*/_jsxs(StyledScroll, {
                  paddingX: 2.25,
                  children: [/*#__PURE__*/_jsx(FormField, {
                    name: "search",
                    size: "small",
                    component: TextField,
                    type: "search",
                    fullWidth: true,
                    onChange: searchHandler(form),
                    placeholder: t('components.modals.addNewGroup.search'),
                    clearable: true,
                    label: ""
                  }), /*#__PURE__*/_jsxs(StyledCheckBoxContainer, {
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    paddingY: 3.25,
                    paddingX: 2.25,
                    children: [/*#__PURE__*/_jsxs(Box, {
                      display: "flex",
                      alignItems: "center",
                      children: [/*#__PURE__*/_jsx(FormField, {
                        variant: "outlined",
                        type: "checkbox",
                        iconColor: "frenchGray",
                        name: "selectAll",
                        component: CheckboxField,
                        onChange: selectAll(values, form)
                      }), /*#__PURE__*/_jsx(Typography, {
                        variant: "body1",
                        fontWeight: "bold",
                        color: "mineShaft",
                        ml: 2.8,
                        children: t('components.modals.addNewGroup.selectAll')
                      })]
                    }), /*#__PURE__*/_jsxs(Typography, {
                      variant: "body1",
                      fontWeight: "bold",
                      color: "mineShaft",
                      children: [values.users.length, ' ', getEnding(values.users.length, Ending.User), ' ', t('components.table.selected')]
                    })]
                  }), filteredUsers === null || filteredUsers === void 0 ? void 0 : filteredUsers.map(function (user) {
                    return /*#__PURE__*/_jsxs(StyledCheckBoxContainer, {
                      display: "flex",
                      alignItems: "center",
                      paddingY: 3.25,
                      paddingX: 2.25,
                      children: [/*#__PURE__*/_jsx(FormField, {
                        variant: "outlined",
                        type: "checkbox",
                        iconColor: "frenchGray",
                        name: "users",
                        value: user.id,
                        component: CheckboxField,
                        onChange: selectUsers(values, form)
                      }), /*#__PURE__*/_jsx(Typography, {
                        variant: "body1",
                        color: "comet",
                        fontWeight: "500",
                        ml: 2.8,
                        children: user.user_name
                      })]
                    }, user.id);
                  })]
                })
              }), /*#__PURE__*/_jsxs(Box, {
                pt: value !== t('components.modals.addNewGroup.members') ? 21.25 : 2.5,
                width: "100%",
                children: [value === t('components.modals.addNewGroup.members') && /*#__PURE__*/_jsx(StyledDivider, {}), /*#__PURE__*/_jsx(Button, {
                  variant: "contained",
                  disabled: pristine || submitting || invalid,
                  fullWidth: true,
                  onClick: handleSubmit(values),
                  children: modalData !== null && modalData !== void 0 && modalData.isEdit ? t('components.modals.addNewUser.save') : t('components.modals.addNewUser.add')
                })]
              })]
            });
          }
        })
      })
    })]
  });
};

export { AddNewGroupModal };